import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, formatMoney } from "../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Truck, Gauge, AlertTriangle, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const EMPTY = { name: "", number: "", expected_avg_kmpl: 0, notes: "", active: true };

export default function Vehicles() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [v, a] = await Promise.all([
        api.get("/vehicles"),
        api.get("/diesel/alerts"),
      ]);
      setList(v.data);
      setAlerts(a.data.alerts || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(EMPTY); setEditingId(null); setShowForm(true); };
  const startEdit = (v) => {
    setForm({
      name: v.name, number: v.number,
      expected_avg_kmpl: v.expected_avg_kmpl || 0,
      notes: v.notes || "", active: v.active !== false,
    });
    setEditingId(v.id); setShowForm(true);
  };
  const cancel = () => { setShowForm(false); setEditingId(null); setForm(EMPTY); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.number.trim()) { toast.error("Vehicle number required"); return; }
    const payload = { ...form, expected_avg_kmpl: Number(form.expected_avg_kmpl) || 0 };
    try {
      if (editingId) {
        await api.patch(`/vehicles/${editingId}`, payload);
        toast.success("Vehicle updated");
      } else {
        await api.post("/vehicles", payload);
        toast.success("Vehicle added");
      }
      cancel(); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete vehicle ${v.number}? Existing diesel entries will keep referring to it.`)) return;
    try {
      await api.delete(`/vehicles/${v.id}`);
      toast.success("Deleted"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="vehicles-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Truck size={26} strokeWidth={2.5} />
          <div>
            <h1 className="font-heading font-black text-2xl sm:text-3xl tracking-tight">Vehicles</h1>
            <p className="text-xs text-gray-500">Track each vehicle's expected diesel average and detect deviations</p>
          </div>
        </div>
        {!showForm && (
          <button onClick={startNew} className="btn-primary h-10 flex items-center gap-2" data-testid="add-vehicle-btn">
            <Plus size={16} /> Add Vehicle
          </button>
        )}
      </div>

      {alerts.length > 0 && (
        <div className="card-flat p-4 bg-[#FEF3C7] border-[#FDE68A]" data-testid="diesel-alerts-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-[#B45309] shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-heading font-bold text-base text-[#B45309]">
                {alerts.length} diesel entr{alerts.length === 1 ? "y" : "ies"} with significant deviation (last 60 days)
              </div>
              <div className="mt-2 space-y-1 text-sm">
                {alerts.slice(0, 5).map((a, i) => (
                  <div key={i} className="flex items-center gap-2 flex-wrap">
                    <span className="data-num text-xs text-gray-700">{a.date}</span>
                    <span className="font-bold">{a.vehicle_number}</span>
                    <span className="text-xs">
                      {a.actual_avg_kmpl} kmpl vs expected {a.expected_avg_kmpl} kmpl
                    </span>
                    <span className={`text-xs font-bold ${a.flag === "low" ? "text-[#B91C1C]" : "text-[#15803D]"}`}>
                      ({a.deviation_pct > 0 ? "+" : ""}{a.deviation_pct}%)
                    </span>
                  </div>
                ))}
                {alerts.length > 5 && <div className="text-xs text-gray-500">+ {alerts.length - 5} more…</div>}
              </div>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} className="card-flat p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="vehicle-form">
          <div>
            <label className="small-label block mb-1">Vehicle Number *</label>
            <input className="input-lg uppercase" placeholder="MH14KQ8335" value={form.number}
              onChange={(e) => setForm({ ...form, number: e.target.value.toUpperCase() })}
              required data-testid="vehicle-number" />
          </div>
          <div>
            <label className="small-label block mb-1">Vehicle Name / Model</label>
            <input className="input-lg" placeholder="Tata Ace - Ambhai" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="vehicle-name" />
          </div>
          <div>
            <label className="small-label block mb-1">Expected Avg (km / litre) *</label>
            <input type="number" step="0.1" className="input-lg data-num text-right"
              value={form.expected_avg_kmpl}
              onChange={(e) => setForm({ ...form, expected_avg_kmpl: e.target.value })}
              required data-testid="vehicle-avg" />
          </div>
          <div className="sm:col-span-2 lg:col-span-3">
            <label className="small-label block mb-1">Notes</label>
            <input className="input-lg" value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="vactive" checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            <label htmlFor="vactive" className="text-sm font-semibold">Active (show in expense entry)</label>
          </div>
          <div className="sm:col-span-2 lg:col-span-3 flex justify-end gap-2">
            <button type="button" onClick={cancel} className="h-11 px-5 border border-gray-300 rounded-sm font-semibold text-sm">Cancel</button>
            <button type="submit" className="btn-primary h-11 px-6" data-testid="vehicle-save">{editingId ? "Update" : "Add Vehicle"}</button>
          </div>
        </form>
      )}

      <div className="card-flat">
        <div className="overflow-auto">
          <table className="table-dense w-full">
            <thead>
              <tr>
                <th className="text-left">Number</th>
                <th className="text-left">Name</th>
                <th className="num">Expected kmpl</th>
                <th className="text-left">Status</th>
                <th className="text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="5" className="py-6 text-center text-gray-400">Loading…</td></tr>}
              {!loading && list.length === 0 && (
                <tr><td colSpan="5" className="py-10 text-center text-gray-400 text-sm">
                  No vehicles yet. Click <strong>Add Vehicle</strong> above.
                </td></tr>
              )}
              {list.map((v) => (
                <tr key={v.id} data-testid={`veh-row-${v.id}`}>
                  <td className="font-bold data-num">{v.number}</td>
                  <td className="text-gray-600">{v.name || "—"}</td>
                  <td className="num data-num">
                    <span className="inline-flex items-center gap-1">
                      <Gauge size={14} className="text-gray-400" /> {formatMoney(v.expected_avg_kmpl)}
                    </span>
                  </td>
                  <td>
                    {v.active
                      ? <span className="text-xs font-bold text-[#15803D]">ACTIVE</span>
                      : <span className="text-xs font-bold text-gray-400">INACTIVE</span>}
                  </td>
                  <td className="text-right whitespace-nowrap">
                    <Link to={`/vehicles/${v.id}`} className="text-xs font-semibold text-gray-600 hover:text-black inline-flex items-center gap-1 mr-3" data-testid={`veh-stats-${v.id}`}>
                      Stats <ArrowRight size={12} />
                    </Link>
                    {isAdmin && (
                      <>
                        <button onClick={() => startEdit(v)} className="text-xs font-semibold text-gray-600 hover:text-black inline-flex items-center gap-1 mr-3">
                          <Pencil size={12} /> Edit
                        </button>
                        <button onClick={() => remove(v)} className="text-xs font-semibold text-[#B91C1C] hover:text-black inline-flex items-center gap-1">
                          <Trash2 size={12} /> Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
